import { useQuery } from '@tanstack/react-query'

import { QUERY_KEYS } from '@/constants/queryKeys'
import { getKemCardEncrypted } from '../walletApi'
import { adaptKemCardEncrypted } from '../walletAdapter'
import {
  AdaptedGetKemCardEncryptedParams,
  AdaptedKemCardEncryptedDTO,
} from '../walletTypes'

interface UseGetKemCardEncryptedOptions {
  /** Whether to enable the query */
  enabled?: boolean
}

/**
 * Hook for fetching and decrypting card data from RAIN provider
 *
 * This hook:
 * 1. Fetches encrypted card data from the API using session_id
 * 2. Decrypts card number and CVV client-side using secret_key
 * 3. Returns adapted DTO ready for UI display
 *
 * @param params - Card ID, session ID, and secret key for decryption
 * @param options - Query options (enabled)
 * @returns Query result with decrypted card data
 *
 * @example
 * ```tsx
 * const { data: session } = useGenerateSessionId()
 *
 * const { data: cardData, isLoading } = useGetKemCardEncrypted(
 *   {
 *     card_id: kemCardId,
 *     session_id: session?.sessionId || '',
 *     secret_key: session?.secretKey || '',
 *   },
 *   { enabled: isDisplayingInfos && !!session }
 * )
 *
 * // Use cardData.cardNumberDecrypted and cardData.cvvDecrypted
 * ```
 */
export const useGetKemCardEncrypted = (
  params: AdaptedGetKemCardEncryptedParams,
  options?: UseGetKemCardEncryptedOptions,
) => {
  return useQuery<AdaptedKemCardEncryptedDTO, Error>({
    queryKey: [
      QUERY_KEYS.KEM_CARD_ENCRYPTED,
      params.card_id,
      params.session_id,
    ],
    queryFn: async () => {
      // Fetch encrypted data from API
      const encryptedResponse = await getKemCardEncrypted({
        card_id: params.card_id,
        session_id: params.session_id,
      })

      // Decrypt and adapt the response
      return adaptKemCardEncrypted(encryptedResponse, params)
    },
    // Only enable when we have all required params
    enabled:
      options?.enabled !== false &&
      !!params.card_id &&
      !!params.session_id &&
      !!params.secret_key,
    // Cache for 5 minutes (same as session)
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Don't refetch on window focus for security
    refetchOnWindowFocus: false,
    // Retry once on failure
    retry: 1,
  })
}
